/**
 * The day's files, summarised before the doctor sees each patient.
 *
 * The requirement: the secretary runs the AI over all of the day's files at
 * once (or a selection), and the summaries are waiting on the doctor's
 * dashboard. The server takes the batch and works through it one file at a
 * time in the background (routes/ai.js — a dozen analyses would outlive any
 * request), so this panel starts it, follows its progress, and lists what has
 * landed. The doctor's live view reads the same summaries; nothing is copied.
 *
 * It speaks the live console's language, because it is the same analysis:
 * the model is chosen the same way ("الأسرع" — the standard ladder, or
 * "الأذكى" — the premium model, for an account an admin granted it, within
 * its daily quota), each summary is drawn on severity rails with the model's
 * thinking one tap away (AiSummaryCard), and any one file can be analysed
 * live, streaming, from its row.
 *
 * A summary is written for the doctor as a starting point. It is not a
 * diagnosis, and the panel says so under every one.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, EmptyState, Icon } from '../../components/common';
import { aiBatchApi } from '../../api/endpoints';
import { useNotifications } from '../../context/NotificationContext';
import { usePoll } from '../../hooks/usePolling';
import { useAiCapabilities } from '../ai/useAiAnalysis';
import { AiSummaryCard } from '../ai/AiSummaryCard';
import { AiAnalyzeButton } from '../ai/AiAnalyzeButton';
import { shortModel } from '../ai/reportDocument';

const MAX_BATCH = 15;

/** "الأسرع" / "الأذكى" — and why the second may not be available. */
function ModelSwitch({ premium, onChange, disabled }) {
  const capabilities = useAiCapabilities();
  const { canUsePremium, usage, premiumDailyLimit, premiumModelLabel, standardModelLabel, loading } = capabilities;
  const quotaSpent = premiumDailyLimit > 0 && usage.premium >= premiumDailyLimit;
  const premiumBlocked = !canUsePremium || quotaSpent;

  useEffect(() => {
    if (premium && premiumBlocked && !loading) onChange(false);
  }, [premium, premiumBlocked, loading, onChange]);

  return (
    <div className="flex flex-col gap-1">
      <div className="ai-model-switch" role="group" aria-label="اختيار نموذج التحليل">
        <button type="button" data-tier="standard" aria-pressed={!premium} disabled={disabled} onClick={() => onChange(false)}>
          <Icon name="zap" size={14} /> الأسرع
        </button>
        <button
          type="button"
          data-tier="premium"
          aria-pressed={premium}
          disabled={disabled || premiumBlocked}
          onClick={() => onChange(true)}
          title={!canUsePremium ? 'النموذج المتقدم غير مُفعّل لحسابك' : quotaSpent ? 'انتهت حصة اليوم' : 'تحليل أعمق بالنموذج المتقدم'}
        >
          <Icon name={premiumBlocked ? 'lock' : 'crown'} size={14} /> الأذكى
        </button>
      </div>
      <span className="flex flex-wrap items-center gap-x-1.5 text-[10.5px] text-slate-400">
        {premium && premiumModelLabel ? (
          <bdi dir="ltr">{shortModel(premiumModelLabel)}</bdi>
        ) : standardModelLabel ? (
          <bdi dir="ltr">{shortModel(standardModelLabel)}</bdi>
        ) : null}
        {premium && premiumDailyLimit > 0 && <span>· استُخدم {usage.premium} من {premiumDailyLimit} اليوم</span>}
        {!canUsePremium && !loading && <span>· «الأذكى» غير مُفعّل لحسابك — يفعّله مدير المنصة</span>}
      </span>
    </div>
  );
}

export function AiBatchPanel({ bookings, date }) {
  const { success, error: toastError } = useNotifications();
  const [summaries, setSummaries] = useState([]);
  const [progress, setProgress] = useState(null);
  const [picked, setPicked] = useState(() => new Set());
  const [open, setOpen] = useState(null);
  const [starting, setStarting] = useState(false);
  const [premium, setPremium] = useState(false);

  const byBooking = useMemo(() => new Map(summaries.map((s) => [String(s.bookingId), s])), [summaries]);

  const loadSummaries = useCallback(() => aiBatchApi.summaries({ date }).then((r) => setSummaries(Array.isArray(r) ? r : [])), [date]);

  useEffect(() => {
    loadSummaries().catch(() => {});
    aiBatchApi
      .status()
      .then((r) => setProgress(r.progress))
      .catch(() => {});
  }, [loadSummaries]);

  // Default selection: everybody today without a summary yet.
  useEffect(() => {
    setPicked(new Set(bookings.filter((b) => !byBooking.has(String(b.id))).slice(0, MAX_BATCH).map((b) => String(b.id))));
  }, [bookings, byBooking]);

  const running = Boolean(progress && !progress.finishedAt);

  usePoll(
    async () => {
      const r = await aiBatchApi.status();
      setProgress(r.progress);
      await loadSummaries();
      if (r.progress?.finishedAt) success(`اكتمل التحليل: ${r.progress.done} ملف`);
    },
    running ? 4000 : null,
    { immediate: false },
  );

  const toggle = (id) =>
    setPicked((set) => {
      const next = new Set(set);
      if (next.has(id)) next.delete(id);
      else if (next.size < MAX_BATCH) next.add(id);
      return next;
    });

  const pickAll = () => setPicked(new Set(bookings.slice(0, MAX_BATCH).map((b) => String(b.id))));
  const pickNone = () => setPicked(new Set());

  const start = async () => {
    setStarting(true);
    try {
      const r = await aiBatchApi.start([...picked], { premium });
      setProgress(r.progress);
      if (r.progress?.downgraded && r.progress.downgradeMessage) toastError(r.progress.downgradeMessage);
      success(`بدأ تحليل ${r.queued.length} ملف — ستظهر الملخصات عند الطبيب تباعاً`);
    } catch (err) {
      toastError(err.message);
    } finally {
      setStarting(false);
    }
  };

  if (bookings.length === 0) {
    return <EmptyState icon="sparkles" title="لا توجد ملفات اليوم" message="يظهر هنا مرضى اليوم لتحليل ملفاتهم قبل دخولهم للطبيب." />;
  }

  const pct = progress && progress.total ? Math.round(((progress.done + progress.failed) / progress.total) * 100) : 0;
  const ready = bookings.filter((b) => byBooking.has(String(b.id))).length;

  return (
    <div className="space-y-4">
      <div className="ai-batch-hero">
        <div className="ai-grid" aria-hidden="true" />
        <div className="relative z-10 flex flex-wrap items-center gap-4">
          <div className="ai-orb" data-tier={premium ? 'premium' : 'standard'} aria-hidden="true">
            {running && (
              <>
                <span className="ai-orb__ring" />
                <span className="ai-orb__ring" />
                <span className="ai-orb__ring" />
              </>
            )}
            <span className="ai-orb__core" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-lg font-black text-white">التحليل الذكي لملفات اليوم</p>
            <p className="text-xs text-slate-300">
              اختر حتى {MAX_BATCH} ملفاً ونموذج التحليل. يصل ملخص كل مريض إلى شاشة الطبيب قبل دخوله، ومعه مسار تفكير النموذج.
            </p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <Badge className="border-purple-400/30 bg-purple-500/10 text-purple-200" icon="sparkles">
                {ready} ملخص جاهز
              </Badge>
              <Badge className="border-cyan-400/30 bg-cyan-500/10 text-cyan-200" icon="users">
                {bookings.length} مريض اليوم
              </Badge>
            </div>
          </div>
          <ModelSwitch premium={premium} onChange={setPremium} disabled={running} />
          <div className="flex flex-col gap-2">
            <Button icon="wand-sparkles" loading={starting} disabled={running || picked.size === 0} onClick={start}>
              تحليل {picked.size} ملف
            </Button>
            <div className="flex gap-1">
              <button type="button" className="ai-card__tool" onClick={pickAll} disabled={running}>
                <Icon name="check-square" size={12} /> الكل
              </button>
              <button type="button" className="ai-card__tool" onClick={pickNone} disabled={running}>
                <Icon name="square" size={12} /> لا شيء
              </button>
            </div>
          </div>
        </div>

        {progress && (
          <div className="relative z-10 mt-4 flex flex-wrap items-center gap-4 rounded-xl border border-white/10 bg-white/[0.03] p-3" aria-live="polite">
            <div className="ai-ring" style={{ '--pct': pct }}>
              <span>{pct}%</span>
            </div>
            <div className="min-w-0 flex-1 text-xs text-slate-300">
              <p className="font-bold text-white">
                {running ? (
                  <>
                    <Icon name="loader-2" size={13} className="me-1 inline animate-spin text-cyan-300" />
                    جارٍ التحليل{progress.current?.patientName ? `: ${progress.current.patientName}` : '…'}
                  </>
                ) : (
                  'اكتمل آخر تحليل'
                )}
              </p>
              <p className="mt-1">
                {progress.done} تم · {progress.failed} تعذر · من {progress.total}
                {progress.tier && <> · {progress.tier === 'premium' ? 'الأذكى' : 'الأسرع'}</>}
              </p>
              {progress.stoppedReason && <p className="mt-1 text-amber-300">{progress.stoppedReason}</p>}
            </div>
          </div>
        )}
      </div>

      <ul className="space-y-2">
        {bookings.map((b) => {
          const s = byBooking.get(String(b.id));
          const id = String(b.id);
          const working = running && progress?.current?.bookingId === id;
          const state = working ? 'working' : s ? 'ready' : 'none';
          return (
            <li key={b.id} className="ai-batch-row p-3" data-state={state}>
              <div className="flex flex-wrap items-center gap-3">
                <input
                  type="checkbox"
                  className="h-5 w-5 accent-purple-500"
                  checked={picked.has(id)}
                  disabled={running}
                  onChange={() => toggle(id)}
                  aria-label={`تحليل ملف ${b.patientName}`}
                />
                <span className="shrink-0 rounded-lg bg-white/5 px-2 py-1 font-mono text-xs text-cyan-300" dir="ltr">
                  {b.time}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm font-bold text-white">{b.patientName}</span>
                {working ? (
                  <Badge className="border-cyan-400/40 bg-cyan-500/15 text-cyan-200" icon="loader-2">
                    يُحلَّل الآن
                  </Badge>
                ) : s ? (
                  <button type="button" onClick={() => setOpen(open === id ? null : id)} aria-expanded={open === id}>
                    <Badge className="border-purple-500/30 bg-purple-500/10 text-purple-200" icon={open === id ? 'chevron-up' : 'sparkles'}>
                      {open === id ? 'إخفاء الملخص' : 'عرض الملخص'}
                    </Badge>
                  </button>
                ) : (
                  <span className="text-xs text-slate-500">لم يُحلل</span>
                )}
                <div className="w-full sm:w-auto">
                  <AiAnalyzeButton
                    patientId={b.patientId}
                    manualPatientCode={b.manualPatientCode}
                    bookingId={b.id}
                    patientName={b.patientName}
                    compact
                    label="تحليل مباشر"
                  />
                </div>
              </div>
              {s && open === id && (
                <div className="mt-3">
                  <AiSummaryCard summary={s} patientName={b.patientName} />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default AiBatchPanel;
